/**
 * AppVersion - Display and manage app version information
 * Shows current version, PWA status, and update options
 */

import React, { useState, useEffect, memo } from 'react';
import { FiInfo, FiDownload, FiWifi, FiWifiOff, FiSmartphone, FiGlobe, FiRefreshCw } from 'react-icons/fi';

// App version from package.json or env
const APP_VERSION = import.meta.env.VITE_APP_VERSION || '0.9.0';
const BUILD_DATE = import.meta.env.VITE_BUILD_DATE || new Date().toISOString().split('T')[0];

interface PWAStatus {
  isInstalled: boolean;
  isOnline: boolean;
  isStandalone: boolean;
  serviceWorkerStatus: 'active' | 'installing' | 'waiting' | 'none';
  cacheStatus: 'ready' | 'updating' | 'none';
}

/**
 * Hook to get PWA installation and status information
 */
export function usePWAStatus(): PWAStatus {
  const [status, setStatus] = useState<PWAStatus>({
    isInstalled: false,
    isOnline: navigator.onLine,
    isStandalone: false,
    serviceWorkerStatus: 'none',
    cacheStatus: 'none',
  });

  useEffect(() => {
    // Check if running as standalone PWA
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;

    // Check if installed via beforeinstallprompt
    const isInstalled = isStandalone || localStorage.getItem('pwa-installed') === 'true';

    setStatus(prev => ({ ...prev, isInstalled, isStandalone }));

    // Listen for online/offline
    const handleOnline = () => setStatus(prev => ({ ...prev, isOnline: true }));
    const handleOffline = () => setStatus(prev => ({ ...prev, isOnline: false }));

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check service worker status
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(registration => {
        let swStatus: PWAStatus['serviceWorkerStatus'] = 'none';

        if (registration.active) swStatus = 'active';
        else if (registration.installing) swStatus = 'installing';
        else if (registration.waiting) swStatus = 'waiting';

        setStatus(prev => ({
          ...prev,
          serviceWorkerStatus: swStatus,
          cacheStatus: swStatus === 'active' ? 'ready' : 'none'
        }));

        // Listen for updates
        registration.addEventListener('updatefound', () => {
          setStatus(prev => ({ ...prev, cacheStatus: 'updating' }));
        });
      });
    }

    // Listen for display mode change
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleDisplayModeChange = (e: MediaQueryListEvent) => {
      setStatus(prev => ({ ...prev, isStandalone: e.matches, isInstalled: e.matches }));
    };
    mediaQuery.addEventListener('change', handleDisplayModeChange);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      mediaQuery.removeEventListener('change', handleDisplayModeChange);
    };
  }, []);

  return status;
}

/**
 * Compact version badge for header/footer
 */
export const VersionBadge = memo(() => {
  const { isOnline, serviceWorkerStatus } = usePWAStatus();

  return (
    <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-full text-xs text-gray-600">
      <span className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-500' : 'bg-red-500'}`} />
      <span>v{APP_VERSION}</span>
      {serviceWorkerStatus === 'active' && (
        <span className="text-green-600" title="Offline ready">●</span>
      )}
    </div>
  );
});

VersionBadge.displayName = 'VersionBadge';

/**
 * Full version info panel for Settings page
 */
export const AppVersionInfo = memo(() => {
  const status = usePWAStatus();
  const [checking, setChecking] = useState(false);

  const checkForUpdates = async () => {
    setChecking(true);
    try {
      // Use DryPWA's SW registration handle if available, else fallback
      const { getSWRegistration } = await import('../../dry/pwa');
      const sw = getSWRegistration();
      if (sw) {
        await sw.update();
      } else if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        await registration.update();
      }
      // Small delay to show checking state
      await new Promise(resolve => setTimeout(resolve, 1000));
    } catch (error) {
      console.error('Error checking for updates:', error);
    }
    setChecking(false);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
          <FiInfo className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">ChecksAndTicks</h3>
          <p className="text-sm text-gray-500">Version {APP_VERSION}</p>
        </div>
      </div>

      {/* Status Grid */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {/* Connection Status */}
        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
          {status.isOnline ? (
            <FiWifi className="w-5 h-5 text-green-600" />
          ) : (
            <FiWifiOff className="w-5 h-5 text-red-600" />
          )}
          <div>
            <p className="text-sm font-medium text-gray-900">
              {status.isOnline ? 'Online' : 'Offline'}
            </p>
            <p className="text-xs text-gray-500">Connection status</p>
          </div>
        </div>

        {/* Install Status */}
        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
          {status.isStandalone ? (
            <FiSmartphone className="w-5 h-5 text-blue-600" />
          ) : (
            <FiGlobe className="w-5 h-5 text-gray-600" />
          )}
          <div>
            <p className="text-sm font-medium text-gray-900">
              {status.isStandalone ? 'Installed' : 'Browser'}
            </p>
            <p className="text-xs text-gray-500">
              {status.isStandalone ? 'Running as app' : 'Running in browser'}
            </p>
          </div>
        </div>

        {/* Service Worker Status */}
        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
          <div className={`w-5 h-5 rounded-full flex items-center justify-center ${status.serviceWorkerStatus === 'active' ? 'bg-green-100' :
            status.serviceWorkerStatus === 'waiting' ? 'bg-yellow-100' : 'bg-gray-100'
            }`}>
            <span className={`w-2 h-2 rounded-full ${status.serviceWorkerStatus === 'active' ? 'bg-green-500' :
              status.serviceWorkerStatus === 'waiting' ? 'bg-yellow-500' : 'bg-gray-400'
              }`} />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">
              {status.serviceWorkerStatus === 'active' ? 'Ready' :
                status.serviceWorkerStatus === 'waiting' ? 'Update Available' :
                  status.serviceWorkerStatus === 'installing' ? 'Installing...' : 'Not Available'}
            </p>
            <p className="text-xs text-gray-500">Offline support</p>
          </div>
        </div>

        {/* Cache Status */}
        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
          <FiDownload className={`w-5 h-5 ${status.cacheStatus === 'ready' ? 'text-green-600' :
            status.cacheStatus === 'updating' ? 'text-yellow-600' : 'text-gray-400'
            }`} />
          <div>
            <p className="text-sm font-medium text-gray-900">
              {status.cacheStatus === 'ready' ? 'Cached' :
                status.cacheStatus === 'updating' ? 'Updating...' : 'Not Cached'}
            </p>
            <p className="text-xs text-gray-500">App resources</p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <div className="text-xs text-gray-500">
          Build: {BUILD_DATE}
        </div>
        <button
          onClick={checkForUpdates}
          disabled={checking}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
        >
          <FiRefreshCw className={`w-4 h-4 ${checking ? 'animate-spin' : ''}`} />
          {checking ? 'Checking...' : 'Check for Updates'}
        </button>
      </div>
    </div>
  );
});

AppVersionInfo.displayName = 'AppVersionInfo';

/**
 * Minimal footer version text
 */
export const FooterVersion = memo(() => (
  <span className="text-xs text-gray-400">
    v{APP_VERSION}
  </span>
));

FooterVersion.displayName = 'FooterVersion';

export default AppVersionInfo;
